import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Consultas sobre Servidores: mini consola de queries de solo lectura.
// Las pestanas viven en localStorage para que el usuario no pierda su trabajo
// al recargar; todo lo demas se pide al backend en cada uso.

const TABS_KEY = 'siimed.queryTabs.v2';
const MAX_TABS = 12;
// Tope de nodos pintados en el arbol; por encima se pide filtrar.
const MAX_TREE_NODES = 400;

const newTab = (n) => ({
  id: `t${Date.now()}${Math.random().toString(16).slice(2, 6)}`,
  name: `Consulta ${n}`,
  sql: '',
  database: '',
});

const loadTabs = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(TABS_KEY));
    if (Array.isArray(raw) && raw.length) return raw.slice(0, MAX_TABS);
  } catch (e) { /* storage bloqueado o corrupto: se arranca limpio */ }
  return [newTab(1)];
};

export default function QueryServers({ apiBase, getToken, canQuery, canAdmin, onClose }) {
  const [servers, setServers] = useState([]);
  const [serverId, setServerId] = useState('');
  const [loadingServers, setLoadingServers] = useState(true);
  const [banner, setBanner] = useState(null);

  // arbol
  const [databases, setDatabases] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [openDb, setOpenDb] = useState(null);
  const [openBranch, setOpenBranch] = useState(null);
  const [objects, setObjects] = useState({});
  const [treeFilter, setTreeFilter] = useState('');

  // pestanas
  const [tabs, setTabs] = useState(loadTabs);
  const [activeId, setActiveId] = useState(() => loadTabs()[0].id);
  const [renaming, setRenaming] = useState(null);

  // ejecucion
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [sqlError, setSqlError] = useState(null);

  // administracion
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminServers, setAdminServers] = useState(null);
  const [form, setForm] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const gutterRef = useRef(null);
  const editorRef = useRef(null);

  const active = tabs.find((t) => t.id === activeId) || tabs[0];

  useEffect(() => {
    try { localStorage.setItem(TABS_KEY, JSON.stringify(tabs)); } catch (e) { /* no critico */ }
  }, [tabs]);

  const call = useCallback(async (functionName, payload) => {
    const token = await getToken();
    const r = await fetch(`${apiBase}/execute`, {
      method: 'POST',
      headers: { Authorization: token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ functionName, ...payload }),
    });
    let data;
    try { data = await r.json(); } catch (e) { data = { error: 'Respuesta no valida del servidor.' }; }
    if (!r.ok) throw new Error(data.error || `Error HTTP ${r.status}`);
    return data;
  }, [apiBase, getToken]);

  // ---------------------------------------------------------------- carga

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const d = await call('ConsultaServidores', { action: 'list-servers' });
        if (cancel) return;
        setServers(d.servers || []);
        if ((d.servers || []).length === 1) setServerId(d.servers[0].serverId);
      } catch (e) {
        if (!cancel) setBanner({ type: 'error', text: e.message });
      } finally {
        if (!cancel) setLoadingServers(false);
      }
    })();
    return () => { cancel = true; };
  }, [call]);

  const loadDatabases = useCallback(async (sid) => {
    setDatabases(null); setObjects({}); setOpenDb(null); setOpenBranch(null);
    if (!sid) return;
    setTreeLoading(true);
    try {
      const d = await call('ConsultaServidores', { action: 'tree-databases', serverId: sid });
      if (d.error) { setBanner({ type: 'error', text: d.error }); setDatabases([]); }
      else setDatabases(d.databases || []);
    } catch (e) {
      setBanner({ type: 'error', text: e.message }); setDatabases([]);
    } finally { setTreeLoading(false); }
  }, [call]);

  useEffect(() => {
    setBanner(null);
    if (serverId) loadDatabases(serverId);
  }, [serverId, loadDatabases]);

  const toggleDb = async (db) => {
    if (openDb === db) { setOpenDb(null); setOpenBranch(null); return; }
    setOpenDb(db); setOpenBranch(null);
    if (objects[db]) return;
    setObjects((o) => ({ ...o, [db]: { loading: true } }));
    try {
      const d = await call('ConsultaServidores', {
        action: 'tree-objects', serverId, database: db,
      });
      setObjects((o) => ({
        ...o,
        [db]: d.error
          ? { error: d.error }
          : { tables: d.tables || [], views: d.views || [] },
      }));
    } catch (e) {
      setObjects((o) => ({ ...o, [db]: { error: e.message } }));
    }
  };

  // ------------------------------------------------------------ pestanas

  const updateActive = (patch) =>
    setTabs((ts) => ts.map((t) => (t.id === active.id ? { ...t, ...patch } : t)));

  const addTab = () => {
    if (tabs.length >= MAX_TABS) {
      setBanner({ type: 'error', text: `Maximo ${MAX_TABS} pestanas.` });
      return;
    }
    const t = newTab(tabs.length + 1);
    setTabs((ts) => [...ts, t]);
    setActiveId(t.id);
    setResult(null); setSqlError(null);
  };

  const closeTab = (id) => {
    setTabs((ts) => {
      const rest = ts.filter((t) => t.id !== id);
      const next = rest.length ? rest : [newTab(1)];
      if (id === activeId) setActiveId(next[0].id);
      return next;
    });
  };

  const insertObject = (full) => {
    const stmt = `SELECT TOP 100 *\nFROM ${full}`;
    const sql = active.sql.trim() ? `${active.sql}\n\n${stmt}` : stmt;
    updateActive({ sql, database: openDb || active.database });
    if (editorRef.current) editorRef.current.focus();
  };

  // ------------------------------------------------------------ ejecucion

  const run = async (validateOnly = false) => {
    if (!serverId) { setBanner({ type: 'error', text: 'Selecciona un servidor.' }); return; }
    if (!active.sql.trim()) { setBanner({ type: 'error', text: 'La consulta esta vacia.' }); return; }
    setRunning(true); setSqlError(null); setBanner(null);
    if (!validateOnly) setResult(null);
    try {
      const d = await call('ConsultaServidores', {
        action: validateOnly ? 'validate-sql' : 'execute-query',
        serverId,
        database: active.database || openDb || '',
        sql: active.sql,
      });
      if (d.error) { setSqlError(d); return; }
      if (validateOnly) {
        setBanner({ type: 'success', text: 'Sintaxis valida.' });
        return;
      }
      setResult(d);
      const nota = d.truncated
        ? ` (recortado a ${d.rowCount} filas${d.truncatedBySize ? ' por tamano' : ''})`
        : '';
      setBanner({ type: 'success', text: `${d.rowCount} filas en ${d.elapsedMs} ms${nota}` });
    } catch (e) {
      setBanner({ type: 'error', text: e.message });
    } finally { setRunning(false); }
  };

  const onEditorKey = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); run(false); }
  };

  const syncGutter = (e) => {
    if (gutterRef.current) gutterRef.current.scrollTop = e.target.scrollTop;
  };

  const lineCount = useMemo(() => active.sql.split('\n').length, [active.sql]);

  // -------------------------------------------------------------- export

  const exportCsv = () => {
    if (!result || !result.rows.length) return;
    const sep = ';'; // Excel en es-CO usa punto y coma como separador de lista
    const esc = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [result.columns.map((c) => esc(c.name)).join(sep)];
    result.rows.forEach((r) => lines.push(r.map(esc).join(sep)));
    const blob = new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const srv = servers.find((s) => s.serverId === serverId);
    const slug = `${(srv ? srv.description : 'consulta')}_${active.name}`
      .replace(/[^A-Za-z0-9_\-]+/g, '_').slice(0, 60);
    a.href = url;
    a.download = `${slug}_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ------------------------------------------------------------- admin

  const openAdmin = async () => {
    setAdminOpen(true); setForm(null); setTestResult(null);
    try {
      const d = await call('GestionServidores', { action: 'admin-list' });
      setAdminServers(d.servers || []);
    } catch (e) {
      setBanner({ type: 'error', text: e.message }); setAdminServers([]);
    }
  };

  const emptyForm = () => ({
    serverId: '', description: '', ip: '', port: 1433,
    username: 'siimed', password: 'abcd1234!', active: true, isNew: true,
  });

  const saveServer = async () => {
    setBusy(true); setTestResult(null);
    try {
      const payload = { action: 'admin-save', ...form };
      delete payload.isNew;
      if (!form.isNew && !form.password) delete payload.password;
      const d = await call('GestionServidores', payload);
      setBanner({ type: 'success', text: d.message });
      setForm(null);
      await openAdmin();
      const ls = await call('ConsultaServidores', { action: 'list-servers' });
      setServers(ls.servers || []);
    } catch (e) {
      setTestResult({ ok: false, error: e.message });
    } finally { setBusy(false); }
  };

  const deleteServer = async (s) => {
    if (!window.confirm(`Eliminar el servidor "${s.description}"?\nSe borrara tambien su clave guardada.`)) return;
    setBusy(true);
    try {
      const d = await call('GestionServidores', { action: 'admin-delete', serverId: s.serverId });
      setBanner({ type: 'success', text: d.message });
      if (serverId === s.serverId) setServerId('');
      await openAdmin();
      const ls = await call('ConsultaServidores', { action: 'list-servers' });
      setServers(ls.servers || []);
    } catch (e) {
      setBanner({ type: 'error', text: e.message });
    } finally { setBusy(false); }
  };

  const testServer = async () => {
    setBusy(true); setTestResult(null);
    try {
      const body = form.isNew
        ? { action: 'admin-test', ip: form.ip, port: form.port, username: form.username, password: form.password }
        : { action: 'admin-test', serverId: form.serverId };
      const d = await call('GestionServidores', body);
      setTestResult(d);
    } catch (e) {
      setTestResult({ ok: false, error: e.message });
    } finally { setBusy(false); }
  };

  // ------------------------------------------------------------- render

  const filtered = (arr) => {
    const f = treeFilter.trim().toLowerCase();
    if (!f) return arr;
    return arr.filter((o) => o.full.toLowerCase().includes(f));
  };

  // Hay servidores con miles de bases (uno llega a 7.083): sin filtro ni tope
  // el arbol es inmanejable y el render se cae a pedazos.
  const matchingDbs = useMemo(() => {
    const all = databases || [];
    const f = treeFilter.trim().toLowerCase();
    if (!f) return all;
    // Una base sigue visible si coincide ella o si ya cargamos objetos que coinciden.
    return all.filter((db) => {
      if (db.name.toLowerCase().includes(f)) return true;
      const node = objects[db.name];
      if (!node || !node.tables) return false;
      return [...node.tables, ...(node.views || [])]
        .some((o) => o.full.toLowerCase().includes(f));
    });
  }, [databases, treeFilter, objects]);

  const visibleDbs = useMemo(
    () => matchingDbs.slice(0, MAX_TREE_NODES), [matchingDbs],
  );

  return (
    <div className="result-panel qs-root">
      <div className="qs-header">
        <h2>Consultas sobre Servidores</h2>
        <div className="qs-header-actions">
          {canAdmin && (
            <button className="btn btn-sm btn-info" onClick={adminOpen ? () => setAdminOpen(false) : openAdmin}>
              {adminOpen ? 'Volver a consultas' : 'Administrar servidores'}
            </button>
          )}
          <button className="btn btn-sm btn-stop" onClick={onClose}>Cerrar</button>
        </div>
      </div>

      {banner && (
        <div className={`message-box ${banner.type === 'success' ? 'message-success' : 'message-error'}`}>
          {banner.text}
        </div>
      )}

      {adminOpen ? (
        <div className="qs-admin">
          {!form && (
            <>
              <button className="btn btn-start btn-sm" onClick={() => setForm(emptyForm())}>
                + Nuevo servidor
              </button>
              <table className="qs-admin-table">
                <thead>
                  <tr>
                    <th>Descripcion (clientes)</th><th>IP</th><th>Puerto</th>
                    <th>Usuario</th><th>Estado</th><th>Actualizado</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {(adminServers || []).map((s) => (
                    <tr key={s.serverId}>
                      <td>{s.description}</td>
                      <td className="qs-mono">{s.ip}</td>
                      <td>{s.port}</td>
                      <td className="qs-mono">{s.username}</td>
                      <td>
                        <span className={s.active ? 'qs-pill qs-pill-on' : 'qs-pill qs-pill-off'}>
                          {s.active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="qs-dim">{(s.updatedAt || '').slice(0, 16).replace('T', ' ')}</td>
                      <td className="qs-row-actions">
                        <button className="btn btn-sm btn-info"
                          onClick={() => { setForm({ ...s, password: '', isNew: false }); setTestResult(null); }}>
                          Editar
                        </button>
                        <button className="btn btn-sm btn-stop" disabled={busy}
                          onClick={() => deleteServer(s)}>Eliminar</button>
                      </td>
                    </tr>
                  ))}
                  {adminServers && adminServers.length === 0 && (
                    <tr><td colSpan={7} className="qs-empty">No hay servidores configurados.</td></tr>
                  )}
                </tbody>
              </table>
            </>
          )}

          {form && (
            <div className="qs-form">
              <h3>{form.isNew ? 'Nuevo servidor' : `Editar: ${form.description}`}</h3>
              <label>Descripcion (clientes que contiene)
                <input value={form.description} maxLength={200}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Ej: Bilimarta, CESDE, Guatavita" />
              </label>
              <div className="qs-form-row">
                <label>IP del servidor
                  <input value={form.ip} className="qs-mono"
                    onChange={(e) => setForm({ ...form, ip: e.target.value })}
                    placeholder="164.152.43.162" />
                </label>
                <label>Puerto
                  <input type="number" value={form.port} min={1} max={65535}
                    onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} />
                </label>
              </div>
              <div className="qs-form-row">
                <label>Usuario de consulta
                  <input value={form.username} className="qs-mono"
                    onChange={(e) => setForm({ ...form, username: e.target.value })} />
                </label>
                <label>Clave de consulta
                  <input type="password" value={form.password} className="qs-mono"
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder={form.isNew ? '' : 'Dejar vacio para conservar la actual'} />
                </label>
              </div>
              <label className="qs-check">
                <input type="checkbox" checked={!!form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Servidor activo (visible para los usuarios de consulta)
              </label>

              {testResult && (
                <div className={`message-box ${testResult.ok ? 'message-success' : 'message-error'}`}>
                  {testResult.ok
                    ? `Conexion correcta como "${testResult.login}" en ${testResult.elapsedMs} ms. ${(testResult.version || '').split('\n')[0]}`
                    : testResult.error}
                </div>
              )}

              <div className="qs-form-actions">
                <button className="btn btn-info btn-sm" onClick={testServer} disabled={busy}>
                  Probar conexion
                </button>
                <button className="btn btn-start btn-sm" onClick={saveServer} disabled={busy}>
                  {busy ? 'Guardando...' : 'Guardar'}
                </button>
                <button className="btn btn-stop btn-sm" onClick={() => { setForm(null); setTestResult(null); }}>
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      ) : !canQuery ? (
        <p className="qs-empty">
          Tu usuario administra servidores pero no tiene habilitada la ejecucion de consultas.
        </p>
      ) : (
        <>
          <div className="qs-toolbar">
            <label className="qs-server-pick">
              Servidor
              <select value={serverId} onChange={(e) => setServerId(e.target.value)}
                disabled={loadingServers}>
                <option value="">
                  {loadingServers ? 'Cargando...' : '-- Selecciona un servidor --'}
                </option>
                {servers.map((s) => (
                  <option key={s.serverId} value={s.serverId}>{s.description}</option>
                ))}
              </select>
            </label>
            {openDb && <span className="qs-ctx">Base activa: <strong>{openDb}</strong></span>}
          </div>

          {!loadingServers && servers.length === 0 && (
            <p className="qs-empty">
              No hay servidores activos configurados.
              {canAdmin ? ' Usa "Administrar servidores" para agregar uno.' : ' Contacta a Gerencia de Tecnologia.'}
            </p>
          )}

          <div className="qs-body">
            {/* ---------------- arbol ---------------- */}
            <aside className="qs-tree">
              <input className="qs-tree-filter" placeholder="Filtrar bases y objetos..."
                value={treeFilter} onChange={(e) => setTreeFilter(e.target.value)} />
              {treeLoading && <p className="qs-dim">Cargando bases de datos...</p>}
              {databases && databases.length === 0 && !treeLoading && (
                <p className="qs-dim">Sin bases de datos accesibles.</p>
              )}
              {databases && databases.length > 0 && (
                <p className="qs-dim">
                  {matchingDbs.length === databases.length
                    ? `${databases.length} bases de datos`
                    : `${matchingDbs.length} de ${databases.length} bases`}
                  {matchingDbs.length > MAX_TREE_NODES && (
                    <span className="qs-warn">
                      {` — mostrando ${MAX_TREE_NODES}, filtra para acotar`}
                    </span>
                  )}
                </p>
              )}
              <ul className="qs-tree-list">
                {visibleDbs.map((db) => {
                  const node = objects[db.name];
                  const isOpen = openDb === db.name;
                  return (
                    <li key={db.name}>
                      <button className={`qs-node qs-node-db ${isOpen ? 'is-open' : ''}`}
                        onClick={() => toggleDb(db.name)}>
                        <span className="qs-caret">{isOpen ? '▾' : '▸'}</span>
                        {db.name}
                      </button>
                      {isOpen && node && node.loading && <p className="qs-dim qs-indent">Cargando...</p>}
                      {isOpen && node && node.error && <p className="qs-err qs-indent">{node.error}</p>}
                      {isOpen && node && node.tables && (
                        <ul className="qs-branch">
                          {[['tables', 'Tablas'], ['views', 'Vistas']].map(([key, label]) => {
                            const items = filtered(node[key] || []);
                            const bOpen = openBranch === `${db.name}:${key}`;
                            return (
                              <li key={key}>
                                <button className={`qs-node qs-node-branch ${bOpen ? 'is-open' : ''}`}
                                  onClick={() => setOpenBranch(bOpen ? null : `${db.name}:${key}`)}>
                                  <span className="qs-caret">{bOpen ? '▾' : '▸'}</span>
                                  {label} <span className="qs-count">({items.length})</span>
                                </button>
                                {bOpen && (
                                  <ul className="qs-leaves">
                                    {items.map((o) => (
                                      <li key={o.full}>
                                        <button className="qs-node qs-node-leaf"
                                          title={`Insertar SELECT de ${o.full}`}
                                          onClick={() => insertObject(o.full)}>
                                          {o.full}
                                        </button>
                                      </li>
                                    ))}
                                    {items.length === 0 && <li className="qs-dim qs-indent">Sin coincidencias</li>}
                                  </ul>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </aside>

            {/* ---------------- editor + resultados ---------------- */}
            <section className="qs-main">
              <div className="qs-tabs">
                {tabs.map((t) => (
                  <div key={t.id}
                    className={`qs-tab ${t.id === activeId ? 'is-active' : ''}`}
                    onClick={() => { setActiveId(t.id); setResult(null); setSqlError(null); }}
                    onDoubleClick={() => setRenaming(t.id)}>
                    {renaming === t.id ? (
                      <input autoFocus className="qs-tab-rename" defaultValue={t.name}
                        onBlur={(e) => {
                          const name = e.target.value.trim() || t.name;
                          setTabs((ts) => ts.map((x) => (x.id === t.id ? { ...x, name } : x)));
                          setRenaming(null);
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} />
                    ) : (
                      <>
                        <span>{t.name}</span>
                        <span className="qs-tab-close" title="Cerrar pestana"
                          onClick={(e) => { e.stopPropagation(); closeTab(t.id); }}>&times;</span>
                      </>
                    )}
                  </div>
                ))}
                <button className="qs-tab-add" onClick={addTab} title="Nueva pestana">+</button>
              </div>

              <div className="qs-editor-wrap">
                <div className="qs-gutter" ref={gutterRef}>
                  {Array.from({ length: lineCount }, (_, i) => (
                    <div key={i} className={sqlError && sqlError.sqlLine === i + 1 ? 'qs-gutter-err' : ''}>
                      {i + 1}
                    </div>
                  ))}
                </div>
                <textarea ref={editorRef} className="qs-editor" spellCheck={false}
                  value={active.sql} placeholder="SELECT TOP 100 * FROM dbo.MiTabla"
                  onChange={(e) => updateActive({ sql: e.target.value })}
                  onKeyDown={onEditorKey} onScroll={syncGutter} />
              </div>

              <div className="qs-actions">
                <button className="btn btn-start btn-sm" disabled={running || !serverId}
                  onClick={() => run(false)}>
                  {running ? 'Ejecutando...' : 'Ejecutar  (Ctrl+Enter)'}
                </button>
                <button className="btn btn-info btn-sm" disabled={running || !serverId}
                  onClick={() => run(true)}>Validar sintaxis</button>
                <button className="btn btn-info btn-sm" disabled={!result || !result.rows.length}
                  onClick={exportCsv}>Exportar a Excel (CSV)</button>
                {openDb && (
                  <span className="qs-dim">
                    Se ejecuta sobre <strong>{active.database || openDb}</strong>
                  </span>
                )}
              </div>

              {sqlError && (
                <div className="qs-sqlerror">
                  <div className="qs-sqlerror-head">
                    {sqlError.phase === 'validacion' ? 'Consulta no permitida'
                      : sqlError.phase === 'sintaxis' ? 'Error de sintaxis'
                        : 'Error al ejecutar'}
                  </div>
                  <pre>{sqlError.error}</pre>
                  {(sqlError.sqlNumber || sqlError.sqlLine) && (
                    <div className="qs-dim">
                      {sqlError.sqlNumber ? `Error ${sqlError.sqlNumber}` : ''}
                      {sqlError.sqlSeverity ? `, nivel ${sqlError.sqlSeverity}` : ''}
                      {sqlError.sqlLine ? `, linea ${sqlError.sqlLine}` : ''}
                    </div>
                  )}
                </div>
              )}

              {result && (
                <div className="qs-results">
                  <div className="qs-results-meta">
                    <strong>{result.rowCount}</strong> filas &middot; {result.elapsedMs} ms
                    {result.truncated && (
                      <span className="qs-warn">
                        &nbsp;Resultado recortado en {result.rowCount} filas
                        {result.truncatedBySize ? ' por tamano de respuesta' : ` (limite ${result.maxRows})`}.
                        Refina la consulta con TOP o WHERE.
                      </span>
                    )}
                    {result.moreResultSets && (
                      <span className="qs-warn">&nbsp;Solo se muestra el primer conjunto de resultados.</span>
                    )}
                  </div>
                  <div className="qs-grid-scroll">
                    <table className="qs-grid">
                      <thead>
                        <tr>
                          <th className="qs-grid-num">#</th>
                          {result.columns.map((c, i) => (
                            <th key={i} title={c.type}>
                              {c.name}<span className="qs-coltype">{c.type}</span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {result.rows.map((row, ri) => (
                          <tr key={ri}>
                            <td className="qs-grid-num">{ri + 1}</td>
                            {row.map((v, ci) => (
                              <td key={ci} className={v === null ? 'qs-null' : undefined}>
                                {v === null ? 'NULL' : String(v)}
                              </td>
                            ))}
                          </tr>
                        ))}
                        {result.rows.length === 0 && (
                          <tr>
                            <td className="qs-empty" colSpan={result.columns.length + 1}>
                              La consulta no devolvio filas.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
